/**
 * Minimal real .xlsx (OOXML SpreadsheetML) writer for the admin pages' Export
 * buttons. Mirrors csv.ts' `exportCsv` API so call sites swap 1:1.
 *
 * Why not SheetJS/exceljs: those add hundreds of kB to an already-large bundle.
 * A single worksheet of flat rows only needs a handful of XML parts zipped
 * together — fflate (already in the tree, ~8 kB) does the zipping. Numbers are
 * emitted as numeric cells; everything else as inline strings (no shared-string
 * table needed), so big-int credit amounts and ids keep their exact text.
 */
import { strToU8, zipSync } from 'fflate';

type Cell = string | number | boolean | null | undefined;
type Row = Record<string, Cell>;

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&apos;';
      default:
        return c;
    }
  });
}

/** 0-based column index → spreadsheet column letters (0→A, 26→AA). */
function colLetter(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function cellXml(ref: string, value: Cell): string {
  if (value === null || value === undefined || value === '') {
    return `<c r="${ref}"/>`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${value}</v></c>`;
  }
  const text = escapeXml(String(value));
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
}

function sheetXml(rows: Row[], cols: string[]): string {
  const headerCells = cols
    .map((c, i) => cellXml(`${colLetter(i)}1`, c))
    .join('');
  const bodyRows = rows
    .map((r, ri) => {
      const rowNum = ri + 2; // row 1 is the header
      const cells = cols.map((c, ci) => cellXml(`${colLetter(ci)}${rowNum}`, r[c])).join('');
      return `<row r="${rowNum}">${cells}</row>`;
    })
    .join('');
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetData><row r="1">${headerCells}</row>${bodyRows}</sheetData></worksheet>`
  );
}

/** Excel sheet names: ≤31 chars, none of : \ / ? * [ ]. */
function sanitizeSheetName(name: string): string {
  return name.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Sheet1';
}

export interface XlsxSheet {
  name: string;
  rows: Row[];
  /** Fixes column order + which keys export; omit to take the union of all row keys. */
  columns?: string[];
}

/**
 * De-dupe + trim sheet names to Excel's rules (non-blank, ≤31 chars, unique
 * within the workbook). Collisions get " 2", " 3", … appended, re-trimming so
 * the suffixed name still fits the cap.
 */
function uniqueSheetNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((raw) => {
    const base = sanitizeSheetName(raw);
    const seenCount = seen.get(base) ?? 0;
    seen.set(base, seenCount + 1);
    if (seenCount === 0) return base;
    const suffix = ` ${seenCount + 1}`;
    return (base.slice(0, 31 - suffix.length) + suffix).slice(0, 31);
  });
}

/** Build a multi-sheet workbook — same OOXML parts as `buildXlsx`, one
 *  `xl/worksheets/sheetN.xml` + `<sheet>`/`<Relationship>` entry per sheet. */
export function buildWorkbook(sheets: XlsxSheet[]): Uint8Array {
  const names = uniqueSheetNames(sheets.map((s) => s.name));

  const worksheetOverrides = sheets
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join('');
  const sheetEntries = names
    .map((name, i) => `<sheet name="${escapeXml(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('');
  const relEntries = sheets
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    )
    .join('');

  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
        `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
        `<Default Extension="xml" ContentType="application/xml"/>` +
        `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
        worksheetOverrides +
        `</Types>`,
    ),
    '_rels/.rels': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
        `</Relationships>`,
    ),
    'xl/workbook.xml': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
        `<sheets>${sheetEntries}</sheets></workbook>`,
    ),
    'xl/_rels/workbook.xml.rels': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        relEntries +
        `</Relationships>`,
    ),
  };

  sheets.forEach((sheet, i) => {
    const cols = sheet.columns ?? Array.from(new Set(sheet.rows.flatMap((r) => Object.keys(r))));
    files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(sheetXml(sheet.rows, cols));
  });

  return zipSync(files, { level: 6 });
}

/** Single-sheet workbook — delegates to `buildWorkbook` with one sheet. */
export function buildXlsx(rows: Row[], columns?: string[], sheetName = 'Sheet1'): Uint8Array {
  return buildWorkbook([{ name: sheetName, rows, columns }]);
}

export function downloadBlob(
  filename: string,
  data: Uint8Array | ArrayBuffer | string | Blob,
  mime: string,
): void {
  // TS lib types Uint8Array as ArrayBufferLike-backed (possibly Shared); Blob
  // wants an ArrayBuffer-backed view. fflate always returns a plain
  // ArrayBuffer-backed array, so the cast is safe.
  const blob = new Blob([data as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Build + download a single-sheet workbook. `columns` fixes column order +
 * which keys export; omit to take the union of all row keys. Drop-in for
 * `exportCsv` — same (filename, rows, columns) shape.
 */
export function exportXlsx(filename: string, rows: Row[], columns?: string[]): void {
  const sheetName = filename.replace(/\.xlsx$/i, '').slice(0, 31);
  const data = buildXlsx(rows, columns, sheetName);
  downloadBlob(
    filename,
    data,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/** Build + download a multi-sheet workbook, in `sheets` order. */
export function exportXlsxWorkbook(filename: string, sheets: XlsxSheet[]): void {
  const data = buildWorkbook(sheets);
  downloadBlob(
    filename,
    data,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}
